import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RowActions } from "@/components/dashboard/RowActions";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, FileDown, Upload, Sparkles } from "lucide-react";

const inr = (n) => `₹${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2, maximumFractionDigits:2})}`;
const EMP_EMPTY = { client_id: "", emp_code:"", full_name:"", designation:"", department:"", email:"", phone:"", join_date:"", dob:"", pan:"", uan:"", esic:"", ctc:0, is_active:true };
const PSL_EMPTY = { employee_id:"", period:"", basic:0, hra:0, special_allowance:0, other_earnings:0, pf:0, esi:0, professional_tax:0, tds:0, loan:0, other_deductions:0, days_worked:26, days_lop:0, overtime_hours:0, status:"Paid", remarks:"" };

export default function PayrollHub() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("");
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [compliance, setCompliance] = useState({ totals: {}, rows: [] });

  const [empOpen, setEmpOpen] = useState(false);
  const [empEdit, setEmpEdit] = useState(null);
  const [ef, setEf] = useState(EMP_EMPTY);

  const [pslOpen, setPslOpen] = useState(false);
  const [pslEdit, setPslEdit] = useState(null);
  const [pf, setPf] = useState(PSL_EMPTY);

  const [detail, setDetail] = useState(null); // employee for the drawer
  const [detailPayslips, setDetailPayslips] = useState([]);
  const [detailPerf, setDetailPerf] = useState([]);

  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  useEffect(() => { (async () => {
    const c = (await api.get("/clients")).data; setClients(c);
    if (!isAdmin) setClient(user.client_id);
    else if (c.length) setClient(c[0].client_id);
  })(); }, [isAdmin, user.client_id]);

  useEffect(() => { if (client) reloadAll(); }, [client]);

  const reloadAll = async () => {
    try {
      const params = isAdmin ? { client_id: client } : undefined;
      const [e, p, prf, comp] = await Promise.all([
        api.get("/employees", { params }),
        api.get("/payslips", { params }),
        api.get("/performance", { params }),
        api.get("/compliance/summary", { params }),
      ]);
      setEmployees(e.data); setPayslips(p.data); setPerformance(prf.data); setCompliance(comp.data);
    } catch (err) { toast.error("Failed to load"); }
  };

  const empByCode = useMemo(() => Object.fromEntries(employees.map(e => [e.employee_id, e])), [employees]);
  const clientName = useMemo(() => clients.find(c => c.client_id === client)?.name || "—", [clients, client]);

  // --- Employee ops ---
  const openEmpCreate = () => { setEmpEdit(null); setEf({ ...EMP_EMPTY, client_id: client }); setEmpOpen(true); };
  const openEmpEdit = (e) => { setEmpEdit(e.employee_id); setEf({ ...e }); setEmpOpen(true); };
  const saveEmp = async () => {
    if (!ef.emp_code || !ef.full_name || !ef.designation || !ef.client_id) return toast.error("Employee code, name, designation and client are required");
    try {
      const payload = { ...ef, ctc: Number(ef.ctc)||0 };
      if (empEdit) await api.put(`/employees/${empEdit}`, payload);
      else await api.post("/employees", payload);
      toast.success(empEdit ? "Employee updated" : "Employee added");
      setEmpOpen(false); reloadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delEmp = async (id) => { try { await api.delete(`/employees/${id}`); toast.success("Deleted"); reloadAll(); } catch { toast.error("Failed"); } };

  // --- Payslip ops ---
  const openPslCreate = () => { setPslEdit(null); setPf({ ...PSL_EMPTY, employee_id: employees[0]?.employee_id || "" }); setPslOpen(true); };
  const openPslEdit = (p) => { setPslEdit(p.payslip_id); setPf({ ...p }); setPslOpen(true); };
  const savePsl = async () => {
    if (!pf.employee_id || !pf.period) return toast.error("Employee and period are required");
    try {
      const num = ["basic","hra","special_allowance","other_earnings","pf","esi","professional_tax","tds","loan","other_deductions","days_worked","days_lop","overtime_hours"];
      const payload = { ...pf }; num.forEach(k => payload[k] = Number(payload[k])||0);
      delete payload.payslip_id; delete payload.created_at; delete payload.client_id; delete payload.gross; delete payload.total_deductions; delete payload.net;
      if (pslEdit) await api.put(`/payslips/${pslEdit}`, payload);
      else await api.post("/payslips", payload);
      toast.success(pslEdit ? "Payslip updated" : "Payslip added");
      setPslOpen(false); reloadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delPsl = async (id) => { try { await api.delete(`/payslips/${id}`); toast.success("Deleted"); reloadAll(); } catch { toast.error("Failed"); } };

  // --- CSV upload ---
  const uploadCsv = async () => {
    if (!csvFile) return toast.error("Select a CSV");
    setCsvBusy(true);
    try {
      const fd = new FormData(); fd.append("file", csvFile);
      const r = await api.post(`/payslips/bulk-upload?client_id=${client}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${r.data.created}, skipped ${r.data.skipped}`);
      if (r.data.errors?.length) console.warn("CSV errors:", r.data.errors);
      setCsvOpen(false); setCsvFile(null); reloadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setCsvBusy(false); }
  };

  // --- Detail drawer ---
  const openDetail = async (emp) => {
    setDetail(emp);
    try {
      const [p, prf] = await Promise.all([api.get(`/payslips?employee_id=${emp.employee_id}`), api.get(`/performance?employee_id=${emp.employee_id}`)]);
      setDetailPayslips(p.data); setDetailPerf(prf.data);
    } catch { setDetailPayslips([]); setDetailPerf([]); }
  };

  const compTotals = compliance.totals || {};

  return (
    <div className="space-y-8" data-testid="payroll-hub-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-navy/50">Payroll</div>
          <h1 className="serif-display text-4xl mt-2">{clientName}</h1>
          <p className="text-navy/60 mt-2">{employees.length} employees · {payslips.length} pay slips</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && (
            <Select value={client} onValueChange={setClient}>
              <SelectTrigger className="w-56" data-testid="ph-client"><SelectValue placeholder="Select client"/></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </div>

      <Tabs defaultValue="employees">
        <TabsList className="bg-slateBg">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="tab-payslips">Pay slips</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-6 space-y-4">
          {isAdmin && <div className="flex justify-end"><button className="btn-primary" onClick={openEmpCreate} data-testid="new-emp-btn"><Plus size={14}/> Add employee</button></div>}
          <div className="bg-white border border-navy/10">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Designation</TableHead>
                <TableHead>CTC</TableHead><TableHead>Join date</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {employees.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-navy/50 py-8">No employees on payroll yet.</TableCell></TableRow>}
                {employees.map(e => (
                  <TableRow key={e.employee_id} className="cursor-pointer" onClick={() => openDetail(e)} data-testid={`emp-row-${e.employee_id}`}>
                    <TableCell className="font-mono text-sm">{e.emp_code}</TableCell>
                    <TableCell className="serif-display">{e.full_name}</TableCell>
                    <TableCell>{e.designation}</TableCell>
                    <TableCell>{inr(e.ctc)}</TableCell>
                    <TableCell className="text-navy/60 text-sm">{e.join_date || "—"}</TableCell>
                    <TableCell><Badge className={e.is_active ? "bg-navy text-gold hover:bg-navy" : "bg-slateBg text-navy/60"}>{e.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    <TableCell onClick={(ev)=>ev.stopPropagation()}>
                      {isAdmin ? <RowActions onEdit={() => openEmpEdit(e)} onDelete={() => delEmp(e.employee_id)} editTestid={`edit-emp-${e.employee_id}`} deleteTestid={`del-emp-${e.employee_id}`} itemLabel={e.full_name}/> : <button className="label-eyebrow text-navy/60 hover:text-gold" onClick={() => openDetail(e)}>View →</button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="payslips" className="mt-6 space-y-4">
          {isAdmin && <div className="flex justify-end gap-3">
            <button className="btn-outline" onClick={() => setCsvOpen(true)} data-testid="csv-btn"><Upload size={14}/> CSV bulk upload</button>
            <button className="btn-primary" onClick={openPslCreate} data-testid="new-psl-btn"><Plus size={14}/> New payslip</button>
          </div>}
          <div className="bg-white border border-navy/10">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Period</TableHead><TableHead>Employee</TableHead><TableHead>Gross</TableHead>
                <TableHead>Deductions</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payslips.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-navy/50 py-8">No pay slips yet.</TableCell></TableRow>}
                {payslips.map(p => (
                  <TableRow key={p.payslip_id}>
                    <TableCell className="serif-display">{p.period}</TableCell>
                    <TableCell>{empByCode[p.employee_id]?.full_name || p.employee_id}</TableCell>
                    <TableCell>{inr(p.gross)}</TableCell>
                    <TableCell className="text-red-700">{inr(p.total_deductions)}</TableCell>
                    <TableCell className="text-gold serif-display">{inr(p.net)}</TableCell>
                    <TableCell><Badge className={p.status === "Paid" ? "bg-navy text-gold hover:bg-navy" : "bg-gold text-white hover:bg-gold"}>{p.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <a href={`${API}/payslips/${p.payslip_id}/pdf`} target="_blank" rel="noreferrer" className="text-navy/60 hover:text-gold p-1.5" aria-label="Download payslip PDF" data-testid={`pdf-${p.payslip_id}`}><FileDown size={15}/></a>
                        {isAdmin && <RowActions onEdit={() => openPslEdit(p)} onDelete={() => delPsl(p.payslip_id)} editTestid={`edit-psl-${p.payslip_id}`} deleteTestid={`del-psl-${p.payslip_id}`} itemLabel={`payslip ${p.period}`}/>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-6 space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            {[["PF", compTotals.pf],["ESI", compTotals.esi],["PT", compTotals.professional_tax],["TDS", compTotals.tds]].map(([k,v]) => (
              <div key={k} className="bg-white border border-navy/10 p-6"><div className="label-eyebrow text-navy/50">{k} disbursed</div><div className="serif-display text-3xl mt-2">{inr(v||0)}</div></div>
            ))}
          </div>
          <div className="bg-white border border-navy/10">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Period</TableHead><TableHead>Headcount</TableHead>
                <TableHead>PF</TableHead><TableHead>ESI</TableHead><TableHead>PT</TableHead><TableHead>TDS</TableHead>
                <TableHead>Gross</TableHead><TableHead>Net</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(compliance.rows || []).length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-navy/50 py-8">No payroll runs to summarise yet.</TableCell></TableRow>}
                {(compliance.rows || []).map(r => (
                  <TableRow key={r.period}>
                    <TableCell className="serif-display">{r.period}</TableCell>
                    <TableCell>{r.headcount}</TableCell>
                    <TableCell>{inr(r.pf)}</TableCell><TableCell>{inr(r.esi)}</TableCell>
                    <TableCell>{inr(r.professional_tax)}</TableCell><TableCell>{inr(r.tds)}</TableCell>
                    <TableCell>{inr(r.gross)}</TableCell><TableCell className="text-gold serif-display">{inr(r.net)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="mt-6 space-y-4">
          <div className="bg-white border border-navy/10">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Period</TableHead><TableHead>Employee</TableHead>
                <TableHead>Rating</TableHead><TableHead>Goals met %</TableHead><TableHead>Attendance %</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {performance.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-navy/50 py-8">No performance reviews recorded.</TableCell></TableRow>}
                {performance.map(m => (
                  <TableRow key={m.metric_id}>
                    <TableCell className="serif-display">{m.period}</TableCell>
                    <TableCell>{empByCode[m.employee_id]?.full_name || m.employee_id}</TableCell>
                    <TableCell><Badge className="bg-gold text-white hover:bg-gold">{m.rating.toFixed(1)} / 5</Badge></TableCell>
                    <TableCell>{m.goals_met_pct.toFixed(0)}%</TableCell>
                    <TableCell>{m.attendance_pct.toFixed(0)}%</TableCell>
                    <TableCell className="text-navy/70 max-w-xs truncate">{m.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Employee dialog */}
      <Dialog open={empOpen} onOpenChange={setEmpOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">{empEdit ? "Edit employee" : "Add employee"}</DialogTitle>
            <DialogDescription>Employee master record. Emp code, name and designation are required.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Emp code</Label><Input value={ef.emp_code} onChange={e=>setEf({...ef, emp_code: e.target.value})} data-testid="emp-code"/></div>
            <div><Label>Full name</Label><Input value={ef.full_name} onChange={e=>setEf({...ef, full_name: e.target.value})} data-testid="emp-name"/></div>
            <div><Label>Designation</Label><Input value={ef.designation} onChange={e=>setEf({...ef, designation: e.target.value})} data-testid="emp-designation"/></div>
            <div><Label>Department</Label><Input value={ef.department} onChange={e=>setEf({...ef, department: e.target.value})} data-testid="emp-department"/></div>
            <div><Label>Email</Label><Input value={ef.email} onChange={e=>setEf({...ef, email: e.target.value})} data-testid="emp-email"/></div>
            <div><Label>Phone</Label><Input value={ef.phone} onChange={e=>setEf({...ef, phone: e.target.value})} data-testid="emp-phone"/></div>
            <div><Label>Join date</Label><Input type="date" value={ef.join_date} onChange={e=>setEf({...ef, join_date: e.target.value})} data-testid="emp-join-date"/></div>
            <div><Label>Date of birth</Label><Input type="date" value={ef.dob} onChange={e=>setEf({...ef, dob: e.target.value})} data-testid="emp-dob"/></div>
            <div><Label>PAN</Label><Input value={ef.pan} onChange={e=>setEf({...ef, pan: e.target.value.toUpperCase()})} data-testid="emp-pan"/></div>
            <div><Label>UAN</Label><Input value={ef.uan} onChange={e=>setEf({...ef, uan: e.target.value})} data-testid="emp-uan"/></div>
            <div><Label>ESIC</Label><Input value={ef.esic} onChange={e=>setEf({...ef, esic: e.target.value})} data-testid="emp-esic"/></div>
            <div><Label>Annual CTC (INR)</Label><Input type="number" value={ef.ctc} onChange={e=>setEf({...ef, ctc: e.target.value})} data-testid="emp-ctc"/></div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setEmpOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={saveEmp} data-testid="emp-save">{empEdit ? "Save" : "Add employee"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip dialog */}
      <Dialog open={pslOpen} onOpenChange={setPslOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">{pslEdit ? "Edit payslip" : "New payslip"}</DialogTitle>
            <DialogDescription>Gross, deductions and net are computed automatically.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Employee</Label>
              <Select value={pf.employee_id} onValueChange={v=>setPf({...pf, employee_id: v})} disabled={!!pslEdit}>
                <SelectTrigger><SelectValue placeholder="Select employee"/></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} — {e.emp_code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Period (YYYY-MM)</Label><Input value={pf.period} placeholder="2025-11" onChange={e=>setPf({...pf, period: e.target.value})}/></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div className="space-y-2">
              <div className="label-eyebrow text-navy/60">Earnings</div>
              {[["basic","Basic"],["hra","HRA"],["special_allowance","Special allowance"],["other_earnings","Other earnings"]].map(([k,l]) => (
                <div key={k}><Label>{l}</Label><Input type="number" value={pf[k]} onChange={e=>setPf({...pf, [k]: e.target.value})}/></div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="label-eyebrow text-navy/60">Deductions</div>
              {[["pf","PF"],["esi","ESI"],["professional_tax","Professional Tax"],["tds","TDS"],["loan","Loan / Advance"],["other_deductions","Other"]].map(([k,l]) => (
                <div key={k}><Label>{l}</Label><Input type="number" value={pf[k]} onChange={e=>setPf({...pf, [k]: e.target.value})}/></div>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mt-4">
            <div><Label>Days worked</Label><Input type="number" value={pf.days_worked} onChange={e=>setPf({...pf, days_worked: e.target.value})}/></div>
            <div><Label>LOP days</Label><Input type="number" value={pf.days_lop} onChange={e=>setPf({...pf, days_lop: e.target.value})}/></div>
            <div><Label>Overtime hrs</Label><Input type="number" value={pf.overtime_hours} onChange={e=>setPf({...pf, overtime_hours: e.target.value})}/></div>
            <div>
              <Label>Status</Label>
              <Select value={pf.status} onValueChange={v=>setPf({...pf, status: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Processing">Processing</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Remarks</Label><Input value={pf.remarks} onChange={e=>setPf({...pf, remarks: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setPslOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={savePsl} data-testid="psl-save">{pslEdit ? "Save" : "Create payslip"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV upload */}
      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">Bulk upload pay slips</DialogTitle>
            <DialogDescription>CSV columns: <code>emp_code, period, basic, hra, special_allowance, other_earnings, pf, esi, professional_tax, tds, loan, other_deductions, days_worked, days_lop, overtime_hours, status, remarks</code></DialogDescription>
          </DialogHeader>
          <div><Input type="file" accept=".csv" onChange={(e)=>setCsvFile(e.target.files?.[0]||null)} data-testid="csv-file"/></div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setCsvOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={uploadCsv} disabled={csvBusy || !csvFile} data-testid="csv-upload">{csvBusy ? "Uploading..." : "Upload CSV"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee detail drawer */}
      <Dialog open={!!detail} onOpenChange={(o)=>!o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="emp-detail-dialog">
          <DialogHeader>
            <DialogTitle className="serif-display text-3xl">{detail?.full_name}</DialogTitle>
            <DialogDescription>{detail?.designation} · {detail?.emp_code} · joined {detail?.join_date || "—"}</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-4 gap-3 text-sm">
            <div><div className="label-eyebrow text-navy/50">CTC</div><div className="serif-display">{inr(detail?.ctc)}</div></div>
            <div><div className="label-eyebrow text-navy/50">PAN</div><div className="serif-display">{detail?.pan || "—"}</div></div>
            <div><div className="label-eyebrow text-navy/50">UAN</div><div className="serif-display">{detail?.uan || "—"}</div></div>
            <div><div className="label-eyebrow text-navy/50">ESIC</div><div className="serif-display">{detail?.esic || "—"}</div></div>
          </div>
          <div className="mt-6">
            <div className="label-eyebrow text-gold mb-2 flex items-center gap-2"><Sparkles size={12}/> Payslip history</div>
            <div className="bg-slateBg/50 border border-navy/10">
              <Table>
                <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Gross</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {detailPayslips.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-navy/50 py-4">No pay slips.</TableCell></TableRow>}
                  {detailPayslips.map(p => (
                    <TableRow key={p.payslip_id}>
                      <TableCell className="serif-display">{p.period}</TableCell>
                      <TableCell>{inr(p.gross)}</TableCell>
                      <TableCell className="text-gold serif-display">{inr(p.net)}</TableCell>
                      <TableCell><Badge className="bg-navy text-gold hover:bg-navy">{p.status}</Badge></TableCell>
                      <TableCell className="text-right"><a href={`${API}/payslips/${p.payslip_id}/pdf`} target="_blank" rel="noreferrer" className="label-eyebrow text-navy/70 hover:text-gold inline-flex items-center gap-1"><FileDown size={12}/> PDF</a></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="mt-6">
            <div className="label-eyebrow text-gold mb-2">Performance reviews</div>
            <div className="bg-slateBg/50 border border-navy/10">
              <Table>
                <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Rating</TableHead><TableHead>Goals %</TableHead><TableHead>Attendance %</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                <TableBody>
                  {detailPerf.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-navy/50 py-4">No reviews yet.</TableCell></TableRow>}
                  {detailPerf.map(m => (
                    <TableRow key={m.metric_id}>
                      <TableCell className="serif-display">{m.period}</TableCell>
                      <TableCell><Badge className="bg-gold text-white hover:bg-gold">{m.rating.toFixed(1)} / 5</Badge></TableCell>
                      <TableCell>{m.goals_met_pct.toFixed(0)}%</TableCell>
                      <TableCell>{m.attendance_pct.toFixed(0)}%</TableCell>
                      <TableCell className="text-navy/70 max-w-xs truncate">{m.notes || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
